import type { DesktopEnvironmentBootstrapEnvelope } from "@cmmd-forge/contracts";
import type { KnownEnvironment } from "@cmmd-forge/client-runtime";
import { isLoopbackHostname } from "@cmmd-forge/shared/loopback";
import { readEnvironmentBootstrapEnvelope } from "../bootstrapEnvelope";

export { isLoopbackHostname } from "@cmmd-forge/shared/loopback";

const FORGE_BACKEND_MODE_KEY = "forge_backend_mode";
const FORGE_APEX_URL_KEY = "forge_apex_url";

export interface PrimaryEnvironmentTarget {
  readonly source: KnownEnvironment["source"];
  readonly target: KnownEnvironment["target"];
  /** Bearer token for authenticating with a paired cloud server. */
  readonly bearerToken?: string;
}

function getDesktopLocalEnvironmentBootstrap(): DesktopEnvironmentBootstrapEnvelope | null {
  return readEnvironmentBootstrapEnvelope();
}

function normalizeBaseUrl(rawValue: string): string {
  return new URL(rawValue, window.location.origin).toString();
}

function joinBasePath(httpBaseUrl: string, pathname: string): string {
  const baseUrl = new URL(httpBaseUrl);
  const basePath = baseUrl.pathname.endsWith("/")
    ? baseUrl.pathname.slice(0, -1)
    : baseUrl.pathname;
  return `${basePath}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
}

function swapBaseUrlProtocol(
  rawValue: string,
  nextProtocol: "http:" | "https:" | "ws:" | "wss:",
): string {
  const url = new URL(normalizeBaseUrl(rawValue));
  url.protocol = nextProtocol;
  return url.toString();
}

export function resolveHttpRequestBaseUrl(httpBaseUrl: string): string {
  let currentUrl: URL;
  try {
    currentUrl = new URL(window.location.href);
  } catch {
    return httpBaseUrl;
  }
  const targetUrl = new URL(httpBaseUrl);
  const isHttpCurrentOrigin = currentUrl.protocol === "http:" || currentUrl.protocol === "https:";
  const isLoopbackSwapCandidate =
    isHttpCurrentOrigin &&
    currentUrl.port !== "" &&
    targetUrl.port !== "" &&
    currentUrl.origin !== targetUrl.origin &&
    isLoopbackHostname(currentUrl.hostname) &&
    isLoopbackHostname(targetUrl.hostname);

  if (!isLoopbackSwapCandidate) {
    return httpBaseUrl;
  }

  const configuredDevServerUrl = import.meta.env.VITE_DEV_SERVER_URL?.trim();
  if (!configuredDevServerUrl) {
    return import.meta.env.DEV ? currentUrl.origin : httpBaseUrl;
  }

  const devServerUrl = new URL(configuredDevServerUrl, currentUrl.origin);
  // Treat same-port loopback addresses as the same dev server — a slug domain
  // (forge-<slug>.localhost:8450) and 127.0.0.1:8450 are the same Vite process.
  const isSameDevServer =
    currentUrl.origin === devServerUrl.origin ||
    (isLoopbackHostname(currentUrl.hostname) &&
      isLoopbackHostname(devServerUrl.hostname) &&
      currentUrl.port === devServerUrl.port);
  if (!isSameDevServer) {
    return httpBaseUrl;
  }

  return currentUrl.origin;
}

function resolveConfiguredPrimaryTarget(): PrimaryEnvironmentTarget | null {
  const configuredHttpBaseUrl = import.meta.env.VITE_HTTP_URL?.trim() || undefined;
  const configuredWsBaseUrl = import.meta.env.VITE_WS_URL?.trim() || undefined;

  if (!configuredHttpBaseUrl && !configuredWsBaseUrl) {
    return null;
  }

  const resolvedHttpBaseUrl =
    configuredHttpBaseUrl ??
    (configuredWsBaseUrl?.startsWith("wss:")
      ? swapBaseUrlProtocol(configuredWsBaseUrl, "https:")
      : swapBaseUrlProtocol(configuredWsBaseUrl!, "http:"));
  const resolvedWsBaseUrl =
    configuredWsBaseUrl ??
    (configuredHttpBaseUrl?.startsWith("https:")
      ? swapBaseUrlProtocol(configuredHttpBaseUrl, "wss:")
      : swapBaseUrlProtocol(configuredHttpBaseUrl!, "ws:"));

  return {
    source: "configured",
    target: {
      httpBaseUrl: normalizeBaseUrl(resolvedHttpBaseUrl),
      wsBaseUrl: normalizeBaseUrl(resolvedWsBaseUrl),
    },
  };
}

function resolveWindowOriginPrimaryTarget(): PrimaryEnvironmentTarget {
  const httpBaseUrl = normalizeBaseUrl(window.location.origin);
  const url = new URL(httpBaseUrl);
  if (url.protocol === "http:") {
    url.protocol = "ws:";
  } else if (url.protocol === "https:") {
    url.protocol = "wss:";
  } else {
    throw new Error(`Unsupported HTTP base URL protocol: ${url.protocol}`);
  }
  url.pathname = "/ws";
  return {
    source: "window-origin",
    target: {
      httpBaseUrl,
      wsBaseUrl: url.toString(),
    },
  };
}

function resolveBackendPreferenceTarget(): PrimaryEnvironmentTarget | null {
  try {
    const mode = localStorage.getItem(FORGE_BACKEND_MODE_KEY);
    if (mode !== "apex") return null;
    const rawApexUrl =
      localStorage.getItem(FORGE_APEX_URL_KEY)?.trim() ??
      import.meta.env.VITE_APEX_URL?.trim() ??
      "";
    if (!rawApexUrl) return null;
    const httpBaseUrl = normalizeBaseUrl(rawApexUrl);
    const wsUrl = new URL(httpBaseUrl);
    wsUrl.protocol = httpBaseUrl.startsWith("https:") ? "wss:" : "ws:";
    return {
      source: "configured",
      target: { httpBaseUrl, wsBaseUrl: wsUrl.toString() },
    };
  } catch {
    return null;
  }
}

function resolveDesktopCloudTarget(): PrimaryEnvironmentTarget | null {
  const desktopBootstrap = getDesktopLocalEnvironmentBootstrap();
  if (!desktopBootstrap || desktopBootstrap.primaryMode !== "cloud") {
    return null;
  }
  // Defensive runtime guard — the type guarantees bearerToken, but IPC
  // payloads may be partial or malformed.
  if (!desktopBootstrap.bearerToken) {
    return null;
  }

  return {
    source: "paired-bearer",
    target: {
      httpBaseUrl: normalizeBaseUrl(desktopBootstrap.httpBaseUrl),
      wsBaseUrl: normalizeBaseUrl(desktopBootstrap.wsBaseUrl),
    },
    bearerToken: desktopBootstrap.bearerToken,
  };
}

function resolveDesktopPrimaryTarget(): PrimaryEnvironmentTarget | null {
  const desktopBootstrap = getDesktopLocalEnvironmentBootstrap();
  if (!desktopBootstrap) {
    return null;
  }
  // Cloud mode is handled by resolveDesktopCloudTarget — skip local resolution.
  if (desktopBootstrap.primaryMode === "cloud") {
    return null;
  }

  if (!desktopBootstrap.httpBaseUrl && !desktopBootstrap.wsBaseUrl) {
    return null;
  }
  if (!desktopBootstrap.httpBaseUrl || !desktopBootstrap.wsBaseUrl) {
    throw new Error(
      "Desktop bootstrap must provide both httpBaseUrl and wsBaseUrl for the local environment.",
    );
  }

  return {
    source: "desktop-managed",
    target: {
      httpBaseUrl: normalizeBaseUrl(desktopBootstrap.httpBaseUrl),
      wsBaseUrl: normalizeBaseUrl(desktopBootstrap.wsBaseUrl),
    },
    ...(desktopBootstrap.bearerToken ? { bearerToken: desktopBootstrap.bearerToken } : {}),
  };
}

export function resolvePrimaryEnvironmentHttpUrl(
  pathname: string,
  searchParams?: Record<string, string>,
): string {
  const primaryTarget = readPrimaryEnvironmentTarget();
  if (!primaryTarget) {
    throw new Error("Unable to resolve the primary environment HTTP base URL.");
  }

  const url = new URL(resolveHttpRequestBaseUrl(primaryTarget.target.httpBaseUrl));
  url.pathname = joinBasePath(url.toString(), pathname);
  if (searchParams) {
    url.search = new URLSearchParams(searchParams).toString();
  }
  return url.toString();
}

export function readPrimaryEnvironmentTarget(): PrimaryEnvironmentTarget | null {
  return (
    resolveDesktopCloudTarget() ??
    resolveBackendPreferenceTarget() ??
    resolveDesktopPrimaryTarget() ??
    resolveConfiguredPrimaryTarget() ??
    resolveWindowOriginPrimaryTarget()
  );
}
